import React, { useState, useRef } from 'react';
import classNames from 'classnames';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { Input } from '../../../../components/inputs/Input';
import { BaseInputProps } from './BaseInput';
import useT from '../../../../translation/src/hooks/useT';

export const IntInput: React.FC<BaseInputProps<number>> = ({ instance, onChange, disabled = false, className }) => {
  const { config, value } = instance;
  const mixedText = useT('mixed', { swc: true });
  const mixedStateTooltip = useT('Different values selected', { swc: true });

  const [isFocused, setIsFocused] = useState(false);
  // Seed the draft with the current value — starting empty made the first
  // native spinner interaction step from '' (→ min/1) instead of the value
  // the unfocused input was displaying.
  const [localValue, setLocalValue] = useState<string>(typeof value === 'number' ? value.toString() : '');
  const inputRef = useRef<HTMLInputElement>(null);
  const [prevValue, setPrevValue] = useState(value);

  // Update local value when external value changes, but only when not focused
  if (!isFocused && typeof value === 'number' && value !== prevValue) {
    setLocalValue(value.toString());
    setPrevValue(value);
  } else if (value !== prevValue) {
    setPrevValue(value);
  }

  const applyConstraints = (numValue: number): number => {
    let constrainedValue = Math.round(numValue); // Ensure integer
    if (config.validation?.min !== undefined && constrainedValue < config.validation.min) {
      constrainedValue = Math.round(config.validation.min);
    }
    if (config.validation?.max !== undefined && constrainedValue > config.validation.max) {
      constrainedValue = Math.round(config.validation.max);
    }
    return constrainedValue;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setLocalValue(inputValue);

    const numValue = parseInt(inputValue, 10);
    if (!isNaN(numValue)) {
      const constrainedValue = applyConstraints(numValue);
      onChange(constrainedValue);
    }
  };

  const handleFocus = () => {
    // Editing starts from the value the unfocused input was showing (mixed
    // selections keep starting from an empty draft, as before).
    if (!instance.isMixed && typeof value === 'number') {
      setLocalValue(value.toString());
    }
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
    commitValue();
  };

  const commitValue = () => {
    const numValue = parseInt(localValue, 10);
    if (!isNaN(numValue)) {
      const constrainedValue = applyConstraints(numValue);
      onChange(constrainedValue);
      setLocalValue(constrainedValue.toString());
    } else {
      // Invalid input - revert to current value
      const fallbackValue = typeof value === 'number' ? value.toString() : '0';
      setLocalValue(fallbackValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitValue();
      inputRef.current?.blur();
    }
  };

  const displayValue = isFocused ? localValue : typeof value === 'number' ? value.toString() : '';

  return (
    <div className={classNames('relative flex-1', className)}>
      <Input
        ref={inputRef}
        type="number"
        value={instance.isMixed && !isFocused ? '' : displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={instance.isMixed ? mixedText : ''}
        disabled={disabled || config.disabled}
        className={classNames('w-full', instance.isMixed && !isFocused && 'text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800 italic pr-8')}
        step={1}
        min={config.validation?.min}
        max={config.validation?.max}
      />
      {instance.isMixed && !isFocused && <QuestionMarkCircleIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neon-green-500 pointer-events-none z-20" title={mixedStateTooltip} />}
    </div>
  );
};
